/**
 * PrescriptionsTab.tsx
 *
 * Thin provider switcher for the patient Prescriptions tab. It runs the
 * context query once (GET /patients/{id}/prescriptions/context) and branches
 * on the server's `provider` answer:
 *   - 'daw'     → DawPrescriptions (ScriptSure iframe + activity list)
 *   - 'treatrx' → TreatRxPrescriptions (the legacy TreatRx/Ayva flow,
 *                 unchanged — moved verbatim from this file)
 *   - 'daw' with `treatRxAvailable` → a practice moving from TreatRx: a
 *                 switch opens on ScriptSure, TreatRx's own context is fetched
 *                 the first time someone switches to it, and both views stay
 *                 mounted (hidden, not unmounted) so a session survives the
 *                 round trip. A patient change drops TreatRx again.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { FilterTabs } from '@/components/ui/FilterTabs';
import {
  getDawErrorCode,
  getPrescriptionsContext,
  getTreatRxPrescriptionsContext,
  type DawPrescriptionsContext,
} from '@/services/prescriptions.service';
import { getErrorMessage } from '@/services/apiClient';
import { DawPrescriptions } from './DawPrescriptions';
import { TreatRxPrescriptions } from './TreatRxPrescriptions';

interface PrescriptionsTabProps {
  patientId: number;
}

type Vendor = 'scriptsure' | 'treatrx';

const VENDOR_TABS = [
  { label: 'ScriptSure', value: 'scriptsure' },
  { label: 'TreatRx', value: 'treatrx' },
];

const TREATRX_FALLBACK_MESSAGE =
  'TreatRx could not be opened. Try again in a moment, or switch back to ScriptSure.';

export function PrescriptionsTab({ patientId }: PrescriptionsTabProps) {
  const {
    data: context,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['patient-prescriptions-context', patientId],
    queryFn: () => getPrescriptionsContext(patientId),
  });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (isError || !context) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <p className="text-sm text-rose-600">
          {error instanceof Error ? error.message : 'Failed to load prescriptions.'}
        </p>
      </div>
    );
  }

  if (context.provider === 'daw' && context.treatRxAvailable) {
    return <VendorSwitch patientId={patientId} context={context} />;
  }

  if (context.provider === 'daw') {
    return <DawPrescriptions patientId={patientId} context={context} />;
  }

  return <TreatRxPrescriptions patientId={patientId} context={context} />;
}

interface VendorSwitchProps {
  patientId: number;
  context: DawPrescriptionsContext;
}

function VendorSwitch({ patientId, context }: VendorSwitchProps) {
  // TreatRx mounts on its first use and then stays, so switching back to it
  // does not re-run its sign-in. The choice belongs to one patient: the tab is
  // not keyed by patient, and a new patient must not inherit an open TreatRx
  // view that would quietly run its flow for them.
  const [choice, setChoice] = useState<{ patientId: number; vendor: Vendor; treatRxOpened: boolean }>({
    patientId,
    vendor: 'scriptsure',
    treatRxOpened: false,
  });
  if (choice.patientId !== patientId) {
    // Reset on every patient change (React's adjust-state-while-rendering
    // pattern), so coming back to an earlier patient also opens on ScriptSure.
    setChoice({ patientId, vendor: 'scriptsure', treatRxOpened: false });
  }
  const current = choice.patientId === patientId ? choice : null;
  const vendor: Vendor = current?.vendor ?? 'scriptsure';
  const treatRxOpened = current?.treatRxOpened ?? false;

  function handleChange(value: string) {
    const next = value as Vendor;
    setChoice({ patientId, vendor: next, treatRxOpened: treatRxOpened || next === 'treatrx' });
  }

  return (
    <div className="space-y-4">
      <FilterTabs tabs={VENDOR_TABS} value={vendor} onChange={handleChange} className="w-fit" />
      <div hidden={vendor !== 'scriptsure'}>
        <DawPrescriptions patientId={patientId} context={context} />
      </div>
      {treatRxOpened && (
        <div hidden={vendor !== 'treatrx'}>
          <TreatRxPane patientId={patientId} />
        </div>
      )}
    </div>
  );
}

function TreatRxPane({ patientId }: { patientId: number }) {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['patient-prescriptions-context', patientId, 'treatrx'],
    queryFn: () => getTreatRxPrescriptionsContext(patientId),
    // The context only seeds TreatRx's own sign-in, and this view stays
    // mounted while hidden: refetching on focus would re-request it for
    // nothing (DawPrescriptions treats its session query the same way).
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center bg-white py-12">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  // A loaded view stays put when a later refresh fails: swapping it for an
  // error would drop an open TreatRx session.
  if (data) {
    return <TreatRxPrescriptions patientId={patientId} context={data} />;
  }

  // Only the "TreatRx turned off" refusal carries text meant for staff.
  const message =
    getDawErrorCode(error) === 'TREATRX_NOT_AVAILABLE'
      ? getErrorMessage(error, TREATRX_FALLBACK_MESSAGE)
      : TREATRX_FALLBACK_MESSAGE;
  return (
    <div className="flex flex-col items-center justify-center gap-3 bg-white py-12">
      <p className="text-sm text-rose-600">{message}</p>
      <Button size="sm" variant="secondary" onClick={() => void refetch()} disabled={isFetching}>
        Try again
      </Button>
    </div>
  );
}
